import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import * as orgsApi from '../api/orgs';

const OrgContext = createContext(null);

const ORG_STORAGE_KEY = 'zefnet_selected_org';

export function OrgProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgRole, setOrgRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadOrgs = useCallback(async () => {
    if (!isAuthenticated) {
      setOrgs([]);
      setSelectedOrg(null);
      setOrgRole(null);
      setLoading(false);
      return;
    }

    try {
      const userOrgs = await orgsApi.getOrgs();
      setOrgs(userOrgs);

      const savedOrgId = localStorage.getItem(ORG_STORAGE_KEY);
      const savedOrg = userOrgs.find(o => o.id === savedOrgId);
      
      if (savedOrg) {
        setSelectedOrg(savedOrg);
        setOrgRole(savedOrg.role);
      } else if (userOrgs.length > 0) {
        setSelectedOrg(userOrgs[0]);
        setOrgRole(userOrgs[0].role);
        localStorage.setItem(ORG_STORAGE_KEY, userOrgs[0].id);
      }
    } catch (err) {
      console.error('Failed to load orgs:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  const selectOrg = useCallback((orgId) => {
    const org = orgs.find(o => o.id === orgId);
    if (org) {
      setSelectedOrg(org);
      setOrgRole(org.role);
      localStorage.setItem(ORG_STORAGE_KEY, orgId);
    }
  }, [orgs]);

  const clearOrg = useCallback(() => {
    setSelectedOrg(null);
    setOrgRole(null);
    localStorage.removeItem(ORG_STORAGE_KEY);
  }, []);

  const value = {
    orgs,
    selectedOrg,
    orgRole: selectedOrg?.role || orgRole,
    loading,
    selectOrg,
    clearOrg,
    hasMultipleOrgs: orgs.length > 1
  };

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg() {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
}

export function getOrgHeader(orgId) {
  return { 'x-org-id': orgId };
}
